export function toLowerCase<T extends string>(str: T): Lowercase<T> {
  return str.toLowerCase() as Lowercase<T>
}

export function toUpperCase<T extends string>(str: T): Uppercase<T> {
  return str.toUpperCase() as Uppercase<T>
}

export function capitalize<T extends string>(str: T): Capitalize<T> {
  return (str.charAt(0).toUpperCase() + str.slice(1)) as Capitalize<T>
}

export type CamelCaseKey<K extends string | number | symbol> = K extends `_${infer Private}`
  ? never
  : K extends `${infer A}_${infer B}`
    ? `${Lowercase<A>}${Capitalize<CamelCaseKey<B>>}`
    : K

type SeparatorCase<K extends string | number | symbol, Separator extends string> = K extends `_${infer Private}`
  ? never
  : K extends `${infer A}${infer B}`
    ? `${A extends Capitalize<A> ? Separator : ''}${Lowercase<A>}${SeparatorCase<B, Separator>}`
    : K

export type SnakeCaseKey<K extends string | number | symbol> = SeparatorCase<K, '_'>
export type KebabCaseKey<K extends string | number | symbol> = SeparatorCase<K, '-'>

export type KebabToSnakeCase<T extends string> = T extends `${infer First}-${infer Rest}`
  ? `${Lowercase<First>}_${KebabToSnakeCase<Rest>}`
  : Lowercase<T>

export function toKebabCase<T extends string>(str: T): KebabCaseKey<T> {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase()
    .replace(/-{3,}/g, '--')
    .replace(/^-+|-+$/g, '') as KebabCaseKey<T>
}

export function toSnakeCase<T extends string>(str: T): SnakeCaseKey<T> {
  return str
    .replace(/([A-Z]{2,})/g, (letter) => letter.toLowerCase())
    .replace(/(\s[A-Za-z])/g, (group) => group.toLowerCase().replace(/[\s]/, '_'))
    .replace(/[A-Z]/g, (letter, index) => {
      return index == 0 ? letter.toLowerCase() : '_' + letter.toLowerCase()
    }) as SnakeCaseKey<T>
}

export function toCamelCase<T extends string>(str: T): CamelCaseKey<T> {
  return str.replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace(/[-_]/, '')) as CamelCaseKey<T>
}

export function kebabToSnake<T extends string>(str: T): KebabToSnakeCase<T> {
  return str.replace(/-/g, '_') as KebabToSnakeCase<T>
}

export function kebabToScreamingSnake<T extends string>(str: T): Uppercase<KebabToSnakeCase<T>> {
  return str.replace(/-/g, '_').toUpperCase() as Uppercase<KebabToSnakeCase<T>>
}

/**
 *
 * @returns a replacer function that can be used as the second argument of JSON.stringify to handle circular references
 */
function getCircularReplacer() {
  const seen = new WeakSet()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (_key: string, value: any) => {
    if (typeof Node === 'function' && value instanceof Node) {
      return
    }
    if (typeof value === 'object' && value !== null) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      if (seen.has(value)) {
        return
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      seen.add(value)
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return value
  }
}

/**
 * Serializes data to a JSON string, handling the case where values are circular references
 * @param data anything
 * @param indent JSON.stringify indentation
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function stringify(data: any, indent = 2) {
  return JSON.stringify(data, getCircularReplacer(), indent)
}
